<script setup lang="ts">
import { computed } from 'vue';
import { useMobile } from '@/composables/useMobile';

// Standard dialog wrapper: a centered max-width dialog on desktop that goes
// fullscreen (sliding from the bottom) on phones. Use this instead of a raw
// v-dialog. With `title` the wrapper owns the card and a close toolbar;
// without it, the default slot owns the card and receives `close`.
// Put buttons in the `actions` slot (titled mode): it renders as a pinned
// footer below the scrolling body, so they stay reachable on small screens.
interface Props {
  maxWidth?: string | number;
  title?: string;
  persistent?: boolean;
  scrollable?: boolean;
  // set false for small confirmations that should stay centered on phones
  mobileFullscreen?: boolean;
  // set false when the dialog hosts an out-of-overlay popup (e.g. Google Places
  // autocomplete), whose items the focus trap would otherwise steal clicks from
  retainFocus?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  maxWidth: undefined,
  title: undefined,
  persistent: false,
  scrollable: false,
  mobileFullscreen: true,
  retainFocus: true,
});

const emit = defineEmits<{ close: [] }>();

const model = defineModel<boolean>({ default: false });

const { isPhone } = useMobile();

const fullscreen = computed(() => props.mobileFullscreen && isPhone.value);

function close() {
  model.value = false;
  emit('close');
}
</script>
<template>
  <v-dialog
    v-model="model"
    :fullscreen="fullscreen"
    :max-width="fullscreen ? undefined : maxWidth"
    :transition="fullscreen ? 'dialog-bottom-transition' : 'dialog-transition'"
    :persistent="persistent"
    :scrollable="scrollable"
    :retain-focus="retainFocus"
  >
    <template
      v-if="$slots.activator"
      #activator="activatorProps"
    >
      <slot
        name="activator"
        v-bind="activatorProps"
      />
    </template>
    <v-card
      v-if="title"
      class="d-flex flex-column"
    >
      <v-toolbar
        color="white"
        density="compact"
        class="flex-grow-0"
      >
        <v-toolbar-title class="font-weight-bold">
          {{ title }}
        </v-toolbar-title>
        <v-btn
          icon="mdi-close"
          aria-label="Cerrar"
          @click="close"
        />
      </v-toolbar>
      <v-card-text class="flex-grow-1 overflow-y-auto">
        <slot :close="close" />
      </v-card-text>
      <div
        v-if="$slots.actions"
        class="dialog-actions flex-grow-0 d-flex align-center justify-end ga-2 px-4 py-3"
      >
        <slot
          name="actions"
          :close="close"
        />
      </div>
    </v-card>
    <slot
      v-else
      :close="close"
    />
  </v-dialog>
</template>

<style scoped>
.dialog-actions {
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgb(var(--v-theme-surface));
}
</style>
